import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'components/ui/use-toast';
import { restoreTrashItem } from './api';
import { UNDO_DURATION_MS } from './constants';
import type { TrashType } from './types';

/** Query keys of the lists a restored item reappears in, per type. */
const LIST_KEYS_BY_TYPE: Record<TrashType, readonly (readonly string[])[]> = {
  test: [['test', 'list']],
  // A subject brings its questions back, and a question changes its subject's counter.
  subject: [['subjects'], ['question-bank']],
  question: [['question-bank'], ['subjects']],
  collection: [['collections']],
};

/** A bulk undo restores a few at a time so it stays under the API's rate limit. */
const RESTORE_BATCH_SIZE = 5;

interface UndoableDelete {
  type: TrashType;
  ids: string[];
  /** The toast text, e.g. "Subject deleted". */
  title: string;
}

type ApiError = { response?: { data?: { message?: string } } };

async function restoreAll(type: TrashType, ids: string[]): Promise<PromiseSettledResult<void>[]> {
  const results: PromiseSettledResult<void>[] = [];
  for (let i = 0; i < ids.length; i += RESTORE_BATCH_SIZE) {
    const batch = ids.slice(i, i + RESTORE_BATCH_SIZE);
    results.push(...(await Promise.allSettled(batch.map((id) => restoreTrashItem(type, id)))));
  }
  return results;
}

/**
 * Shows the "deleted" toast with an Undo button. Deleting only hides the item (see docs/TRASH.md);
 * Undo restores it, and after the toast closes it stays recoverable by support until the purge.
 * The toast outlives the component, so Undo uses plain calls instead of a mutation.
 */
export function useUndoDelete() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useCallback(
    ({ type, ids, title }: UndoableDelete) => {
      const undo = async () => {
        const results = await restoreAll(type, ids);
        for (const queryKey of LIST_KEYS_BY_TYPE[type]) void queryClient.invalidateQueries({ queryKey: [...queryKey] });

        const failed = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
        if (failed.length < results.length) {
          toast.success(t('trash.restored'));
          return;
        }
        // The API's message says why (a taken name, a full plan, a missing parent), so show it.
        const reason = (failed[0]?.reason as ApiError | undefined)?.response?.data?.message;
        toast.error(t('trash.restore_failed'), { description: reason });
      };

      toast.success(title, {
        duration: UNDO_DURATION_MS,
        action: { label: t('trash.undo'), onClick: () => void undo() },
      });
    },
    [queryClient, t],
  );
}
